/* eslint-disable no-unused-vars */
import {
  ChangeEventHandler,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import axios from 'axios';
import { useRecoilValue } from 'recoil';

import { bytesToMegaBytes } from 'utils/helpers';
import { IFolder, IFile } from 'typings/document';
import createDocument from 'utils/createDocument';
import { OrganizationStage } from 'typings/enums';

import selectedDateState from 'atoms/selectedDate';
import { OrganizationStagesApiMap } from 'constants/common';
import { FileActionsBag } from './useFiles';
import useGetTokenData from './api/queries/useGetTokenData';

type HookProps = {
  files: IFile[];
  filesActionsBag: FileActionsBag;
  folder: IFolder;
  setOpen: (arg0: boolean) => void;
  reference: string;
};
type HookReturnType = {
  onSubmit: (arg0?: IFile[]) => Promise<void>;
  onCancel: () => void;
  handleAddFiles: ChangeEventHandler<HTMLInputElement>;
  isSubmitting: boolean;
};

export const MAXIMUM_FILE_SIZE = 1000 * 150; // 150 GB

export const checkFileSize = (file: File): boolean =>
  bytesToMegaBytes(file.size) <= MAXIMUM_FILE_SIZE;

let isSubmittingWatcher = false;

window.addEventListener('beforeunload', (e) => {
  if (isSubmittingWatcher) {
    e.preventDefault();
    e.returnValue = '';
  }
});

const ongoingReferenceValue = OrganizationStage.ongoing;

export default function useUploadFiles({
  files,
  filesActionsBag,
  folder,
  setOpen,
  reference,
}: HookProps): HookReturnType {
  const { addRawFiles, updateFiles, resetFiles } = filesActionsBag;
  const isSubmitCanceled = useRef<boolean>(false);
  const currentSubmittions = useRef<number>(0);
  const filesRef = useRef<IFile[]>(files);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const selectedDate = useRecoilValue(selectedDateState);
  const isOngoing = reference === ongoingReferenceValue;
  const progressRef = useRef<Record<string, number[]>>({});

  const { refetch } = useGetTokenData(undefined, selectedDate);

  useEffect(() => {
    filesRef.current = files;
  }, [files]);

  useEffect(() => {
    isSubmittingWatcher = isSubmitting;
  }, [isSubmitting]);

  const onCancel = useCallback(() => {
    isSubmitCanceled.current = true;
    files.forEach((file) => {
      file.cancel?.();
    });
    resetFiles();
    setOpen(false);
  }, [files]);

  const handleAddFiles = useCallback(
    (event) => {
      addRawFiles(Array.from(event.target.files));
    },
    [addRawFiles]
  );

  const onSubmit = useCallback(async (retryFiles: IFile[] | undefined) => {
    isSubmitCanceled.current = false;
    currentSubmittions.current += 1;
    setIsSubmitting(true);

    const filesToUpload = (
      Array.isArray(retryFiles) ? retryFiles : filesRef.current
    ).filter(
      ({ content, progress }) => checkFileSize(content) && progress !== 100
    );

    const uploadPromises: Promise<void>[] = [];
    const filesBatch: IFile[] = [];

    filesToUpload.forEach((file) => {
      const cancelTokenSource = axios.CancelToken.source();
      const decoratedFile = {
        ...file,
        cancel: cancelTokenSource.cancel,
        progress: null,
      };

      filesBatch.push(decoratedFile);
      // const actualReference =

      const uploadPromise = createDocument(
        {
          documentTypeName: folder.name,
          documentTypeReference: OrganizationStagesApiMap[reference as OrganizationStage],
          filename: file.content.name,
          fileSize: file.content.size,
          ...(selectedDate && isOngoing && { dateTag: selectedDate }),
        },
        file.content,
        {
          onUploadProgress: (
            progressEvent: ProgressEvent,
            totalParts: number = 1,
            partKey: number = 0
          ) => {
            const progress =
              ((progressEvent.loaded / totalParts) * 100) / progressEvent.total;

            if (!progressRef.current[file.id]) {
              progressRef.current[file.id] = [];
            }

            progressRef.current[file.id][partKey] = progress;

            const totalFileProgress = progressRef.current[file.id].reduce(
              (a, b = 0) => a + b,
              0
            );

            updateFiles([
              {
                ...decoratedFile,
                progress: Math.max(totalFileProgress - 1, 0),
              },
            ]);
          },
          cancelToken: cancelTokenSource.token,
        },
        () => updateFiles([{ ...file, progress: 100 }]),
        (error) =>
          updateFiles([{ ...file, progress: axios.isCancel(error) ? -2 : -1 }])
      );

      uploadPromises.push(uploadPromise);
    });

    updateFiles(filesBatch);

    const operationsRes = await Promise.allSettled(uploadPromises);
    refetch();

    const allUploaded = operationsRes.every(
      (operation) => operation.status === 'fulfilled'
    );

    currentSubmittions.current -= 1;
    setIsSubmitting(currentSubmittions.current > 0);

    if (
      allUploaded &&
      filesRef.current.every((file) => file.progress === 100)
    ) {
      setTimeout(() => {
        if (!isSubmitCanceled.current) {
          setOpen(false);
          resetFiles();
        }
      }, 1000);
    }
  }, []);

  return { onSubmit, onCancel, handleAddFiles, isSubmitting };
}
